/**
 * Goals Preview — Shows active goals on Life tab, connecting life planning to goals.
 */

import { memo } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Target, ArrowRight } from 'lucide-react'
import type { Goal } from '@turnifly/shared/types'

interface GoalsPreviewProps {
  goals: Goal[]
}

export const GoalsPreview = memo(function GoalsPreview({ goals }: GoalsPreviewProps) {
  const { t } = useTranslation()

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03]">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-success" />
          <h3 className="text-sm font-bold text-text">{t('life.activeGoals')}</h3>
        </div>
        <GoToGoalsButton />
      </div>

      <div className="px-3 pb-3 space-y-2">
        {goals.map((goal, i) => (
          <GoalMiniCard key={goal.id} goal={goal} index={i} />
        ))}
      </div>
    </div>
  )
})

// ── Goal mini card ──

function GoalMiniCard({ goal, index }: { goal: Goal; index: number }) {
  const progress = goal.target_value > 0
    ? Math.min(100, Math.round(((goal.current_value - goal.start_value) / (goal.target_value - goal.start_value)) * 100))
    : 0

  const statusColor = goal.status === 'in_progress' ? '#0061FF' : '#F59E0B'

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08 }}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04]"
    >
      {/* Icon */}
      <span className="text-lg shrink-0">{goal.icon}</span>

      {/* Name + progress */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-text truncate">{goal.name}</p>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1 rounded-full bg-white/[0.04] overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: statusColor }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            />
          </div>
          <span className="text-[10px] font-bold tabular-nums" style={{ color: statusColor }}>
            {progress}%
          </span>
        </div>
      </div>

      {/* Value */}
      <div className="text-right shrink-0">
        <span className="text-[10px] tabular-nums text-hint">
          {goal.current_value}/{goal.target_value}
        </span>
        <p className="text-[8px] text-hint">{goal.unit}</p>
      </div>
    </motion.div>
  )
}

// ── Navigate to goals tab ──

function GoToGoalsButton() {
  const { t } = useTranslation()

  const handleGoToGoals = () => {
    // Dispatch custom event that home-screen listens for to switch tabs
    window.dispatchEvent(new CustomEvent('turnifly:switchHomeTab', { detail: 'goals' }))
  }

  return (
    <button
      onClick={handleGoToGoals}
      className="flex items-center gap-1 text-[10px] font-medium text-primary tap-highlight-transparent"
    >
      {t('life.viewGoals')}
      <ArrowRight className="w-3 h-3" />
    </button>
  )
}
